import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { AsyncSelect, Form, FormField, ModalDialog, Numeric, Select } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import { createLoadOptions } from "../../../../utils/loadOptions";
import useI18n from "../../../../hooks/useTranslations";
import { InsertScrewBody, Screw, UpdateScrewBody } from "../../../../http/beamReinforcement/types/screw";
import { ScrewFamily } from "../../../../http/beamReinforcement/types/screwFamily";
import useBRScrewFamiliesApi from "../../../../http/beamReinforcement/useAdminScrewFamilies";
import { useBRScrewsApi } from "../../../../http/beamReinforcement/useAdminScrews";
import { SearchAdminItem } from "../../../../http/types/items";
import { useItemsApi } from "../../../../http/useItems";
import { PaginationQueryWithFields } from "../../../../types";

const MIN_MEASUREMENT_MM = 0.001;

type ItemOption = Pick<SearchAdminItem, "id" | "code" | "description">;

type ScrewFamilyOption = Pick<ScrewFamily, "id" | "name">;

interface ScrewFormBody {
  item: ItemOption | null;
  screwFamily: ScrewFamilyOption | null;
  length: number | null;
  threadedLength: number | null;
}

const ScrewFormDialog: FC<{ screw?: Screw; onClose: () => void }> = ({ screw, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const screwsApi = useBRScrewsApi();
  const itemsApi = useItemsApi();
  const [defaultItemOptions, setDefaultItemOptions] = useState<ItemOption[]>([]);
  const itemSearchTerm = useRef("");
  const itemAborter = useRef(new AbortController());
  const screwFamiliesApi = useBRScrewFamiliesApi();
  const screwFamiliesAborter = useRef(new AbortController());

  const getItemOptions = (query: PaginationQueryWithFields<ItemOption>, signal: AbortSignal): Promise<ItemOption[]> => {
    return itemsApi.getAdminItems(query, signal).then((items) =>
      items.map((item) => ({
        id: item.id,
        code: item.code,
        description: item.description,
      })),
    );
  };

  const loadItemOptions = createLoadOptions<SearchAdminItem, keyof ItemOption>(
    itemAborter,
    itemSearchTerm,
    getItemOptions,
    {
      fields: ["id", "code", "description"],
    },
  );

  useEffect(() => {
    loadItemOptions("")
      .then(setDefaultItemOptions)
      .catch(() => setDefaultItemOptions([]));
  }, []);

  const { data: screwFamiliesData } = screwFamiliesApi.useBRScrewFamilies(
    { limit: 100 },
    screwFamiliesAborter.current.signal,
  );
  const screwFamilyOptions = useMemo<ScrewFamilyOption[]>(() => screwFamiliesData?.items ?? [], [screwFamiliesData]);

  const defaultValues: ScrewFormBody = {
    item: screw ? { id: screw.item.id, code: screw.item.code, description: screw.item.description } : null,
    screwFamily: screw ? { id: screw.screwFamily.id, name: screw.screwFamily.name } : null,
    length: screw?.length ?? null,
    threadedLength: screw?.threadedLength ?? null,
  };

  const onSubmit = () => async (formData: ScrewFormBody) => {
    if (!formData.item || !formData.screwFamily || formData.length == null || formData.threadedLength == null) {
      return;
    }

    const body: InsertScrewBody | UpdateScrewBody = {
      itemId: formData.item.id,
      screwFamilyId: formData.screwFamily.id,
      length: formData.length,
      threadedLength: formData.threadedLength,
    };

    try {
      if (screw) {
        await screwsApi.updateBRScrew(screw.item.id, body);
      } else {
        await screwsApi.insertBRScrew(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        screw
          ? `${i18n.translation.common.edit} ${i18n.translation.beamReinforcementModule.screws.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.beamReinforcementModule.screws.singular}`
      }
    >
      <Form<ScrewFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
      >
        {({ control, reset }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [screw]);

          return (
            <FormFieldsContainer fullWidth>
              <Controller
                name="item"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                    {({ labelId }) => (
                      <AsyncSelect<ItemOption, false>
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultItemOptions}
                        loadOptions={loadItemOptions}
                        isMulti={false}
                        isDisabled={!!screw}
                        placeholder={`${i18n.translation.common.select}...`}
                        getOptionLabel={(option) => `${option.code} - ${option.description}`}
                        getOptionValue={(option) => option.id}
                        isInvalid={fieldState.invalid}
                        onChange={(option) => field.onChange(option)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="screwFamily"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField
                    label={i18n.translation.beamReinforcementModule.screws.fields.family}
                    error={fieldState.error}
                   
                  >
                    {({ labelId, isInvalid }) => (
                      <Select<ScrewFamilyOption, false>
                        {...field}
                        inputId={labelId}
                        options={screwFamilyOptions}
                        isDisabled={!!screw}
                        placeholder={`${i18n.translation.common.select}...`}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        isInvalid={isInvalid}
                        onChange={(option) => field.onChange(option)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="length"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.length} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="threadedLength"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField
                    label={i18n.translation.beamReinforcementModule.screws.fields.threadedLength}
                    error={fieldState.error}
                   
                  >
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default ScrewFormDialog;
